/**
 * Quantity schedule of values (no dollars): contract line items per project, and progress
 * from daily report work lines that point at an item (`WorkLine.sovItemId`).
 * Counting rule: submitted + approved reports count as installed; drafts show as pending.
 */
import { DAILY_REPORT_UNITS, type DailyReport } from "./daily-report.js";
import type { Cell, Sheet } from "./daily-report.js";

export interface SovItem {
  id: string;
  projectId: string;
  itemNo: string;
  description: string;
  unit: string;
  contractQty: number;
  /** Relative weight for the weighted overall % (default 1). Not money. */
  weight: number;
  /** Pay item number it came from (takeoff seed / import), for matching. */
  payItemNumber?: string;
  notes: string;
  sort: number;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export class SovError extends Error {
  constructor(message: string) { super(message); this.name = "SovError"; }
}

const str = (v: unknown, max: number) => (typeof v === "string" ? v.trim().slice(0, max) : typeof v === "number" ? String(v) : "");
export const parseQty = (v: unknown): number => {
  if (typeof v === "number") return Number.isFinite(v) ? v : NaN;
  const t = String(v ?? "").replace(/[,\s$]/g, "");
  return t === "" ? NaN : Number(t);
};
export const normUnit = (u: unknown): string => {
  const t = str(u, 12).toUpperCase().replace(/\./g, "");
  const alias: Record<string, string> = { TON: "TN", TONS: "TN", T: "TN", "SQ YD": "SY", SQYD: "SY", "LIN FT": "LF", LINFT: "LF", FT: "LF", "CU YD": "CY", CUYD: "CY", EACH: "EA", LUMP: "LS", "LUMP SUM": "LS", GALLON: "GAL", GALLONS: "GAL", "SQ FT": "SF", SQFT: "SF", HOURS: "HR", HRS: "HR" };
  return alias[t] ?? t;
};

export function normalizeSovInput(input: unknown, base?: SovItem): Pick<SovItem, "itemNo" | "description" | "unit" | "contractQty" | "weight" | "payItemNumber" | "notes" | "sort"> {
  const i = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const has = (k: string) => k in i;
  const description = has("description") ? str(i.description, 300) : base?.description ?? "";
  if (!description) throw new SovError("Description is required.");
  const qty = has("contractQty") ? parseQty(i.contractQty) : base?.contractQty ?? NaN;
  if (!Number.isFinite(qty) || qty < 0) throw new SovError("Contract quantity must be a number ≥ 0.");
  const weight = has("weight") ? parseQty(i.weight) : base?.weight ?? 1;
  const unit = has("unit") ? normUnit(i.unit) : base?.unit ?? "";
  if (!unit) throw new SovError("Unit is required (SY, TN, LF, CY, EA…).");
  const pay = has("payItemNumber") ? str(i.payItemNumber, 40) : base?.payItemNumber ?? "";
  return {
    itemNo: has("itemNo") ? str(i.itemNo, 40) : base?.itemNo ?? "",
    description, unit, contractQty: Math.round(qty * 1000) / 1000,
    weight: Number.isFinite(weight) && weight >= 0 ? weight : 1,
    ...(pay ? { payItemNumber: pay } : {}),
    notes: has("notes") ? str(i.notes, 1000) : base?.notes ?? "",
    sort: has("sort") && Number.isFinite(Number(i.sort)) ? Number(i.sort) : base?.sort ?? 0,
  };
}

export interface SovHistoryEntry { reportId: string; date: string; foreman: string; status: DailyReport["status"]; quantity: number; unit: string; location: string; description: string; unitMismatch: boolean }
export interface SovItemProgress {
  item: SovItem;
  /** Approved daily reports only (counts toward % complete). */
  installed: number;
  approved: number;
  /** Drafts and submitted reports waiting for approval. */
  pending: number;
  remaining: number;
  /** installed / contract, 0–100+ (over-run shows above 100). */
  percent: number;
  history: SovHistoryEntry[];
}
export interface SovProgress {
  items: SovItemProgress[];
  /** Average of per-item % (capped at 100), every item counts the same. */
  overallByCount: number;
  /** Same, weighted by each item's `weight`. */
  overallWeighted: number;
  itemsComplete: number;
  /** Work lines tied to an item id that no longer exists. */
  orphanLines: number;
}

const r3 = (n: number) => Math.round(n * 1000) / 1000;
const r1 = (n: number) => Math.round(n * 10) / 10;

export function sovProgress(items: SovItem[], reports: DailyReport[]): SovProgress {
  const byId = new Map(items.map((i) => [i.id, { item: i, installed: 0, approved: 0, pending: 0, remaining: 0, percent: 0, history: [] as SovHistoryEntry[] }]));
  let orphanLines = 0;
  for (const r of [...reports].sort((a, b) => a.date.localeCompare(b.date))) {
    for (const w of r.work) {
      if (!w.sovItemId) continue;
      const p = byId.get(w.sovItemId);
      if (!p) { orphanLines++; continue; }
      const mismatch = normUnit(w.unit) !== normUnit(p.item.unit);
      p.history.push({ reportId: r.id, date: r.date, foreman: r.foreman, status: r.status, quantity: w.quantity, unit: w.unit, location: w.location, description: w.description, unitMismatch: mismatch });
      if (mismatch) continue; // shown in history, never silently added in the wrong unit
      // Only an approved report deducts; drafts and reports awaiting approval show as pending.
      if (r.status === "approved") { p.installed += w.quantity; p.approved += w.quantity; }
      else p.pending += w.quantity;
    }
  }
  const out = [...byId.values()].sort((a, b) => a.item.sort - b.item.sort || a.item.itemNo.localeCompare(b.item.itemNo, undefined, { numeric: true }));
  for (const p of out) {
    p.installed = r3(p.installed); p.approved = r3(p.approved); p.pending = r3(p.pending);
    p.remaining = r3(Math.max(0, p.item.contractQty - p.installed));
    p.percent = p.item.contractQty > 0 ? r1((p.installed / p.item.contractQty) * 100) : p.installed > 0 ? 100 : 0;
  }
  const capped = (p: SovItemProgress) => Math.min(100, p.percent);
  const wsum = out.reduce((a, p) => a + p.item.weight, 0);
  return {
    items: out,
    overallByCount: out.length ? r1(out.reduce((a, p) => a + capped(p), 0) / out.length) : 0,
    overallWeighted: wsum > 0 ? r1(out.reduce((a, p) => a + capped(p) * p.item.weight, 0) / wsum) : 0,
    itemsComplete: out.filter((p) => p.percent >= 100).length,
    orphanLines,
  };
}

/** Workbook for end-of-job billing progress: summary + per-report history. */
export function sovSheets(progress: SovProgress, projectName: string, asOf: string): Sheet[] {
  const rows: Cell[][] = [["Item", "Description", "Unit", "Contract qty", "Installed to date", "Approved", "Pending (drafts)", "Remaining", "% complete", "Weight"]];
  for (const p of progress.items) rows.push([p.item.itemNo, p.item.description, p.item.unit, p.item.contractQty, p.installed, p.approved, p.pending, p.remaining, p.percent / 100, p.item.weight]);
  rows.push([], ["", `Overall (by item count)`, "", "", "", "", "", "", progress.overallByCount / 100], ["", `Overall (weighted)`, "", "", "", "", "", "", progress.overallWeighted / 100], ["", `${projectName} - as of ${asOf}. Installed = submitted + approved daily reports.`]);
  const hist: Cell[][] = [["Item", "Description", "Date", "Foreman", "Status", "Quantity", "Unit", "Location", "Work line", "Counted"]];
  for (const p of progress.items) for (const h of p.history) hist.push([p.item.itemNo, p.item.description, h.date, h.foreman, h.status, h.quantity, h.unit, h.location, h.description, h.unitMismatch ? "No - unit mismatch" : h.status !== "approved" ? "Pending approval" : "Yes"]);
  return [{ name: "Progress", rows }, { name: "History", rows: hist }];
}

// ------------------------------------------------------------------ import

export interface SovImportRow { itemNo: string; description: string; unit: string; contractQty: number }
export interface SovImportResult { rows: SovImportRow[]; skipped: Array<{ line: number; reason: string }>; columns: Record<string, number> }

const HEAD: Record<keyof SovImportRow, RegExp> = {
  itemNo: /^(item|item ?no\.?|item ?#|no\.?|pay ?item|line|bid ?item)/i,
  description: /(desc|description|name|work)/i,
  unit: /^(unit|uom|units|u\/m)$/i,
  contractQty: /(qty|quantity|contract|est|plan)/i,
};

/** Rows from a CSV/XLSX sheet → SOV lines. Finds the header row in the first 10 rows. */
export function parseSovRows(table: Cell[][]): SovImportResult {
  let headerAt = -1; const columns: Record<string, number> = {};
  for (let r = 0; r < Math.min(10, table.length) && headerAt < 0; r++) {
    const cells = (table[r] ?? []).map((c) => String(c ?? "").trim());
    const found: Record<string, number> = {};
    for (const key of ["unit", "contractQty", "itemNo", "description"] as const) {
      const idx = cells.findIndex((c, i) => c && HEAD[key].test(c) && !Object.values(found).includes(i));
      if (idx >= 0) found[key] = idx;
    }
    if (found.description !== undefined && found.unit !== undefined && found.contractQty !== undefined) { headerAt = r; Object.assign(columns, found); }
  }
  if (headerAt < 0) throw new SovError("Couldn't find the header row. It needs columns for Description, Unit and Quantity (Item No. optional).");
  const rows: SovImportRow[] = []; const skipped: SovImportResult["skipped"] = [];
  for (let r = headerAt + 1; r < table.length; r++) {
    const row = table[r] ?? [];
    const get = (k: string) => (columns[k] === undefined ? "" : row[columns[k]!]);
    const description = str(get("description"), 300);
    if (!description && !str(get("itemNo"), 40)) continue;
    const qty = parseQty(get("contractQty"));
    const unit = normUnit(get("unit"));
    if (!description) { skipped.push({ line: r + 1, reason: "no description" }); continue; }
    if (!Number.isFinite(qty)) { skipped.push({ line: r + 1, reason: `quantity "${String(get("contractQty") ?? "")}" isn't a number` }); continue; }
    if (!unit) { skipped.push({ line: r + 1, reason: "no unit" }); continue; }
    rows.push({ itemNo: str(get("itemNo"), 40), description, unit, contractQty: qty });
  }
  return { rows, skipped, columns };
}

/** Minimal RFC 4180 CSV parser. */
export function parseCsv(text: string): string[][] {
  const out: string[][] = []; let row: string[] = []; let cur = ""; let q = false;
  const t = text.replace(/^\ufeff/, "");
  for (let i = 0; i < t.length; i++) {
    const c = t[i]!;
    if (q) { if (c === '"') { if (t[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += c; continue; }
    if (c === '"' && cur === "") q = true;
    else if (c === ",") { row.push(cur); cur = ""; }
    else if (c === "\n" || c === "\r") { if (c === "\r" && t[i + 1] === "\n") i++; row.push(cur); out.push(row); row = []; cur = ""; }
    else cur += c;
  }
  if (cur || row.length) { row.push(cur); out.push(row); }
  return out;
}

export const SOV_UNITS = DAILY_REPORT_UNITS;
